import { useState, useEffect } from "react";
import { useAuth, useTheme } from "../App";
import { useCurrency } from "../contexts/CurrencyContext";
import { useTranslation } from "react-i18next";
import { useApi } from "../components/settings/useApi";
import {
  ProfileSettings,
  RoleSettings,
  AccountInfoSettings,
  AppearanceSettings,
  LanguageSettings,
  CurrencySettings,
  DashboardSettingsCard,
  SubscriptionSettingsCard,
  PayPalSettingsCard,
  EmailSettingsCard,
  EmailTemplatesCard,
  ManagerNotificationsCard,
  CloudStorageSettings,
  OrgEmailSettings
} from "../components/settings";

export default function Settings() {
  var { user, token, checkAuth } = useAuth();
  var { theme, toggleTheme } = useTheme();
  var { currency, updateCurrency, refetchCurrency } = useCurrency();
  var { t, i18n } = useTranslation();
  var [userCurrency, setUserCurrency] = useState(currency);
  var [hasApiKey, setHasApiKey] = useState(false);
  var { fetchJson } = useApi(token);

  var isAdmin = user?.role === "admin";
  var canChangeCurrency = ["admin", "realestate_admin", "realestate_manager"].includes(user?.role);
  var canChangeDashboard = ["admin", "realestate_admin"].includes(user?.role);
  var canConfigureStorage = ["admin", "realestate_admin"].includes(user?.role);
  var canConfigureOrgEmail = ["admin", "realestate_admin"].includes(user?.role);

  useEffect(function() { setUserCurrency(currency); }, [currency]);

  useEffect(function() {
    if (isAdmin) {
      fetchJson("/api/admin/settings/email").then(function(data) {
        setHasApiKey(data.has_api_key || false);
      }).catch(function() {});
    }
  }, [isAdmin]);

  var currentLang = i18n.language || "en";

  return (
    <div className="max-w-2xl space-y-6" data-testid="settings-page">
      <div>
        <h1 className="text-3xl font-bold tracking-tight">{t("settings.title")}</h1>
        <p className="text-muted-foreground mt-1">{t("settings.subtitle")}</p>
      </div>

      <ProfileSettings user={user} token={token} t={t} checkAuth={checkAuth} />
      <RoleSettings user={user} t={t} />
      <LanguageSettings currentLang={currentLang} onLanguageChange={function(lang) { i18n.changeLanguage(lang); }} t={t} />

      {canChangeCurrency && (
        <CurrencySettings currency={currency} userCurrency={userCurrency} setUserCurrency={setUserCurrency} updateCurrency={updateCurrency} refetchCurrency={refetchCurrency} t={t} />
      )}

      {canChangeDashboard && <DashboardSettingsCard token={token} t={t} />}

      <AppearanceSettings theme={theme} toggleTheme={toggleTheme} />
      <AccountInfoSettings user={user} t={t} />

      {isAdmin && (
        <>
          <SubscriptionSettingsCard token={token} t={t} />
          <PayPalSettingsCard token={token} t={t} />
          <EmailSettingsCard token={token} t={t} />
          <ManagerNotificationsCard token={token} t={t} hasApiKey={hasApiKey} />
          <EmailTemplatesCard token={token} t={t} hasApiKey={hasApiKey} />
        </>
      )}

      {canConfigureStorage && <CloudStorageSettings token={token} t={t} />}
      {canConfigureOrgEmail && <OrgEmailSettings token={token} user={user} t={t} />}
    </div>
  );
}
